import { Component, computed, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';

import { CoinAcceptorDto, CoinAcceptorService, CoinAcceptorStatus } from '../../../../../../api/gen';

/** Euro coins the validator can be programmed for, in cents. */
export const EURO_COINS = [10, 20, 50, 100, 200] as const;
const DEFAULT_TIMEOUT_SECONDS = 120;
/** Euros the cash box may hold, until the EMP 800 box has been measured. */
const DEFAULT_CAPACITY_EUROS = 300;
const DEFAULT_ALERT_PERCENT = 80;

/**
 * The payment station's coin validator (EMP 800). Registered here so the kiosk may offer coins; the
 * station's provisioning (`payment.coin_acceptor` in site-config.json) makes the USB device reachable.
 * Coins are offered only while this is enabled AND the station reports the validator Ok.
 *
 * Loaded only when the section is opened — a device group page lists terminals, and most stations
 * have no coin acceptor, so asking for every terminal up front would be a request per card for nothing.
 */
@Component({
  selector: 'app-terminal-coin-acceptor',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule, MatFormFieldModule, MatInputModule, MatProgressSpinnerModule],
  templateUrl: './terminal-coin-acceptor.component.html',
})
export class TerminalCoinAcceptorComponent {
  private readonly api = inject(CoinAcceptorService);
  private readonly snackBar = inject(MatSnackBar);

  readonly terminalId = input.required<string>();
  readonly disabled = input(false);

  readonly euroCoins = EURO_COINS;

  readonly expanded = signal(false);
  readonly loading = signal(false);
  readonly saving = signal(false);
  /** Loaded and known to have none: the register form is shown. */
  readonly loaded = signal(false);
  readonly coinAcceptor = signal<CoinAcceptorDto | null>(null);

  // Form
  readonly deviceName = signal('EMP 800');
  readonly serialNumber = signal('');
  readonly isEnabled = signal(true);
  readonly coinValues = signal<Set<number>>(new Set(EURO_COINS));
  readonly timeoutSeconds = signal(DEFAULT_TIMEOUT_SECONDS);
  /** Edited in euros; the API takes cents. */
  readonly capacityEuros = signal(DEFAULT_CAPACITY_EUROS);
  readonly alertPercent = signal(DEFAULT_ALERT_PERCENT);

  readonly statusClass = computed(() => {
    switch (this.coinAcceptor()?.status) {
      case CoinAcceptorStatus.Ok: return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case CoinAcceptorStatus.Fault: return 'bg-red-50 text-red-700 border-red-200';
      case CoinAcceptorStatus.Missing: return 'bg-amber-50 text-amber-700 border-amber-200';
      default: return 'bg-slate-50 text-slate-600 border-slate-200';
    }
  });

  readonly canSave = computed(() =>
    !!this.deviceName().trim() && this.coinValues().size > 0 &&
    this.timeoutSeconds() >= 30 && this.timeoutSeconds() <= 900 &&
    this.capacityEuros() >= 1 && this.capacityEuros() <= 10000 &&
    this.alertPercent() >= 1 && this.alertPercent() <= 100);

  coinLabel(cents: number): string {
    return new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR' }).format(cents / 100);
  }

  toggle(): void {
    this.expanded.update((v) => !v);
    if (this.expanded() && !this.loaded()) this.load();
  }

  load(): void {
    this.loading.set(true);
    this.api.coinAcceptorsByTerminalTerminalIdGet(this.terminalId()).subscribe({
      next: (dto) => {
        this.loading.set(false);
        this.loaded.set(true);
        this.setForm(dto);
      },
      error: (err) => {
        this.loading.set(false);
        // 404 is the normal answer: this station has no coin acceptor yet.
        if (err?.status === 404) {
          this.loaded.set(true);
          this.setForm(null);
        } else {
          this.snackBar.open('Could not load the coin acceptor', 'Close', { duration: 4000 });
        }
      },
    });
  }

  toggleCoin(cents: number): void {
    const next = new Set(this.coinValues());
    if (next.has(cents)) next.delete(cents);
    else next.add(cents);
    this.coinValues.set(next);
  }

  save(): void {
    if (!this.canSave() || this.saving()) return;
    const existing = this.coinAcceptor();
    const values = [...this.coinValues()].sort((a, b) => a - b);
    this.saving.set(true);

    const request$ = existing?.id
      ? this.api.coinAcceptorsCoinAcceptorIdPut(existing.id, {
          deviceName: this.deviceName().trim(),
          serialNumber: this.serialNumber().trim(),
          isEnabled: this.isEnabled(),
          acceptedCoinValuesInCents: values,
          inactivityTimeoutSeconds: this.timeoutSeconds(),
          cashBoxCapacityInCents: Math.round(this.capacityEuros() * 100),
          cashBoxAlertThresholdPercent: this.alertPercent(),
        })
      : this.api.coinAcceptorsPost({
          paymentTerminalId: this.terminalId(),
          deviceName: this.deviceName().trim(),
          serialNumber: this.serialNumber().trim() || null,
          isEnabled: this.isEnabled(),
          acceptedCoinValuesInCents: values,
          inactivityTimeoutSeconds: this.timeoutSeconds(),
          cashBoxCapacityInCents: Math.round(this.capacityEuros() * 100),
          cashBoxAlertThresholdPercent: this.alertPercent(),
        });

    request$.subscribe({
      next: (dto) => {
        this.saving.set(false);
        this.setForm(dto);
        this.snackBar.open(existing ? 'Coin acceptor saved' : 'Coin acceptor registered', 'Close', { duration: 2500 });
      },
      error: (err) => this.fail(err, 'Could not save the coin acceptor'),
    });
  }

  remove(): void {
    const id = this.coinAcceptor()?.id;
    if (!id || this.saving()) return;
    if (!confirm('Remove the coin acceptor from this station? The kiosk stops offering coins.')) return;

    this.saving.set(true);
    this.api.coinAcceptorsCoinAcceptorIdDelete(id).subscribe({
      next: () => {
        this.saving.set(false);
        this.setForm(null);
        this.snackBar.open('Coin acceptor removed', 'Close', { duration: 2500 });
      },
      error: (err) => this.fail(err, 'Could not remove the coin acceptor'),
    });
  }

  private setForm(dto: CoinAcceptorDto | null): void {
    this.coinAcceptor.set(dto);
    this.deviceName.set(dto?.deviceName || 'EMP 800');
    this.serialNumber.set(dto?.serialNumber ?? '');
    this.isEnabled.set(dto?.isEnabled ?? true);
    this.coinValues.set(new Set(dto?.acceptedCoinValuesInCents?.length ? dto.acceptedCoinValuesInCents : EURO_COINS));
    this.timeoutSeconds.set(dto?.inactivityTimeoutSeconds ?? DEFAULT_TIMEOUT_SECONDS);
    this.capacityEuros.set(dto?.cashBoxCapacityInCents != null ? dto.cashBoxCapacityInCents / 100 : DEFAULT_CAPACITY_EUROS);
    this.alertPercent.set(dto?.cashBoxAlertThresholdPercent ?? DEFAULT_ALERT_PERCENT);
  }

  private fail(err: { error?: unknown } | null, fallback: string): void {
    this.saving.set(false);
    // MadResult errors arrive as a plain-string body ("Title.Detail").
    const body = typeof err?.error === 'string' ? err.error.trim() : '';
    this.snackBar.open(body || fallback, 'Close', { duration: 5000 });
  }
}
